type StaticRoute = {
  pattern: string;
  to: string;
  href: string;
};

type DynamicRoute<TParam extends string> = {
  pattern: string;
  to: (params: Record<TParam, string>) => string;
  href: (params: Record<TParam, string>) => string;
};

function staticRoute(path: string): StaticRoute {
  return {pattern: path, to: path, href: path};
}

function dynamicRoute<TParam extends string>(
  pattern: string,
  build: (params: Record<TParam, string>) => string,
): DynamicRoute<TParam> {
  return {pattern, to: build, href: build};
}

export const PATHS = {
  dashboard: staticRoute('/dashboard'),

  feature_toggles: staticRoute('/feature_toggles'),
  feature_toggle_detail: dynamicRoute<'id'>(
    '/feature_toggles/:id',
    ({id}) => `/feature_toggles/${id}`,
  ),

  bulk_actions: staticRoute('/bulk_actions'),

  dev_tools: staticRoute('/dev_tools'),

  unprovisioned_ebics_accounts: staticRoute('/unprovisioned_ebics_accounts'),

  unallocated_payments: staticRoute('/unallocated_payments'),

  unallocated_outgoing_payments: staticRoute('/unallocated_outgoing_payments'),
  unallocated_outgoing_payment_detail: dynamicRoute<'id'>(
    '/unallocated_outgoing_payments/:id',
    ({id}) => `/unallocated_outgoing_payments/${id}`,
  ),
  unallocated_outgoing_payment_allocate: dynamicRoute<'id'>(
    '/unallocated_outgoing_payments/:id/allocate',
    ({id}) => `/unallocated_outgoing_payments/${id}/allocate`,
  ),

  manager_background_processes: staticRoute('/manager_background_processes'),
  manager_background_process_detail: dynamicRoute<'id'>(
    '/manager_background_processes/:id',
    ({id}) => `/manager_background_processes/${id}`,
  ),

  deposits: staticRoute('/deposits'),
  deposit_detail: dynamicRoute<'id'>(
    '/deposits/:id',
    ({id}) => `/deposits/${id}`,
  ),

  deposit_guarantee_applications: staticRoute(
    '/deposit_guarantee_applications',
  ),
  deposit_guarantee_application_detail: dynamicRoute<'id'>(
    '/deposit_guarantee_applications/:id',
    ({id}) => `/deposit_guarantee_applications/${id}`,
  ),
  deposit_guarantee_application_edit: dynamicRoute<'id'>(
    '/deposit_guarantee_applications/:id/edit',
    ({id}) => `/deposit_guarantee_applications/${id}/edit`,
  ),

  bank_account_viban_projections: staticRoute('/bank_account_vibans'),
  bank_account_viban_projection_detail: dynamicRoute<'id'>(
    '/bank_account_vibans/:id',
    ({id}) => `/bank_account_vibans/${id}`,
  ),

  payment_files: staticRoute('/payment_files'),
  payment_file_detail: dynamicRoute<'id'>(
    '/payment_files/:id',
    ({id}) => `/payment_files/${id}`,
  ),

  deposit_account_vibans: staticRoute('/deposit_account_vibans'),
  deposit_account_viban_detail: dynamicRoute<'id'>(
    '/deposit_account_vibans/:id',
    ({id}) => `/deposit_account_vibans/${id}`,
  ),
  deposit_account_viban_edit: dynamicRoute<'id'>(
    '/deposit_account_vibans/:id/edit',
    ({id}) => `/deposit_account_vibans/${id}/edit`,
  ),

  claims: staticRoute('/claims'),
  claim_detail: dynamicRoute<'id'>('/claims/:id', ({id}) => `/claims/${id}`),

  property_owners: staticRoute('/property_owners'),
  property_owner_create: staticRoute('/property_owners/new'),
  property_owner_detail: dynamicRoute<'id'>(
    '/property_owners/:id',
    ({id}) => `/property_owners/${id}`,
  ),
  property_owner_edit: dynamicRoute<'id'>(
    '/property_owners/:id/edit',
    ({id}) => `/property_owners/${id}/edit`,
  ),

  bank_credit_transfers: staticRoute('/bank_credit_transfers'),
  bank_credit_transfer_detail: dynamicRoute<'id'>(
    '/bank_credit_transfers/:id',
    ({id}) => `/bank_credit_transfers/${id}`,
  ),
  bank_direct_debits: staticRoute('/bank_direct_debits'),
  bank_direct_debit_detail: dynamicRoute<'id'>(
    '/bank_direct_debits/:id',
    ({id}) => `/bank_direct_debits/${id}`,
  ),

  intraday_bank_transactions: staticRoute('/intraday_bank_transactions'),

  bank_transactions: staticRoute('/bank_transactions'),
  bank_transaction_create: staticRoute('/bank_transactions/new'),
  bank_transaction_detail: dynamicRoute<'id'>(
    '/bank_transactions/:id',
    ({id}) => `/bank_transactions/${id}`,
  ),

  bank_account_statements: staticRoute('/bank_account_statements'),
  bank_account_statement_detail: dynamicRoute<'id'>(
    '/bank_account_statements/:id',
    ({id}) => `/bank_account_statements/${id}`,
  ),

  roles: staticRoute('/roles'),
  role_create: staticRoute('/roles/new'),
  role_detail: dynamicRoute<'id'>('/roles/:id', ({id}) => `/roles/${id}`),
  role_edit: dynamicRoute<'id'>(
    '/roles/:id/edit',
    ({id}) => `/roles/${id}/edit`,
  ),

  users: staticRoute('/users'),
  user_create: staticRoute('/users/new-backoffice-user'),
  user_detail: dynamicRoute<'id'>('/users/:id', ({id}) => `/users/${id}`),
  user_edit: dynamicRoute<'id'>(
    '/users/:id/edit',
    ({id}) => `/users/${id}/edit`,
  ),

  everreal_events: staticRoute('/everreal_events'),
  everreal_event_detail: dynamicRoute<'id'>(
    '/everreal_events/:id',
    ({id}) => `/everreal_events/${id}`,
  ),

  inactive_email_recipients: staticRoute('/inactive_email_recipients'),
  impower_events: staticRoute('/impower_events'),
  impower_event_detail: dynamicRoute<'id'>(
    '/impower_events/:id',
    ({id}) => `/impower_events/${id}`,
  ),

  vvrb_events: staticRoute('/vvrb_events'),
  vvrb_event_detail: dynamicRoute<'id'>(
    '/vvrb_events/:id',
    ({id}) => `/vvrb_events/${id}`,
  ),

  vvrb_requests: staticRoute('/vvrb_requests'),
  vvrb_request_detail: dynamicRoute<'id'>(
    '/vvrb_requests/:id',
    ({id}) => `/vvrb_requests/${id}`,
  ),

  rental_contracts: staticRoute('/rental_contracts'),
  rental_contract_detail: dynamicRoute<'id'>(
    '/rental_contracts/:id',
    ({id}) => `/rental_contracts/${id}`,
  ),
  rental_contract_edit: dynamicRoute<'id'>(
    '/rental_contracts/:id/edit',
    ({id}) => `/rental_contracts/${id}/edit`,
  ),

  condominiums: staticRoute('/condominiums'),
  condominium_create: staticRoute('/condominiums/new'),
  condominium_detail: dynamicRoute<'id'>(
    '/condominiums/:id',
    ({id}) => `/condominiums/${id}`,
  ),
  condominium_edit: dynamicRoute<'id'>(
    '/condominiums/:id/edit',
    ({id}) => `/condominiums/${id}/edit`,
  ),

  managers: staticRoute('/managers'),
  manager_create: staticRoute('/managers/new'),
  manager_detail: dynamicRoute<'id'>(
    '/managers/:id',
    ({id}) => `/managers/${id}`,
  ),
  manager_edit: dynamicRoute<'id'>(
    '/managers/:id/edit',
    ({id}) => `/managers/${id}/edit`,
  ),

  organisations: staticRoute('/organisations'),
  organisation_create: staticRoute('/organisations/new'),
  organisation_detail: dynamicRoute<'id'>(
    '/organisations/:id',
    ({id}) => `/organisations/${id}`,
  ),
  organisation_edit: dynamicRoute<'id'>(
    '/organisations/:id/edit',
    ({id}) => `/organisations/${id}/edit`,
  ),

  accounts: staticRoute('/accounts'),
  account_detail: dynamicRoute<'id'>(
    '/accounts/:id',
    ({id}) => `/accounts/${id}`,
  ),
  account_edit: dynamicRoute<'id'>(
    '/accounts/:id/edit',
    ({id}) => `/accounts/${id}/edit`,
  ),
  account_create: staticRoute('/accounts/new'),

  account: staticRoute('/account'),

  login: staticRoute('/login'),
  logout: staticRoute('/logout'),

  ui_showcase: staticRoute('/ui'),
};
